import React, { useEffect, useRef } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { createGlobalStyle } from 'styled-components';
import Router, { useRouter } from 'next/router';
import Script from 'next/script';
import getConfig from 'next/config';
import { isServer } from 'utils/getEnvironment';
import animate from 'animate.css';
import normalize from 'sanitize.css';
import toggler from 'react-toggle/style.css';
import rcslider from 'rc-slider/assets/index.css';
import commoncss from '../src/assets/styles/common.css';
import slickSlider from '../src/assets/styles/slick-slider.css';
import wrapper from '../src/store';
import Toast from '../src/components/Toast';
import NProgress from '../src/components/NProgress';
import CommandPalette from '../src/components/CommandPalette';
import '../sentry.client.config';
import * as SuperTokensConfig from '../config/frontendConfig';
import SuperTokensReact, { SuperTokensWrapper } from 'supertokens-auth-react';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { HelpscoutProvider } from '../src/providers/HelpscoutProvider';
import { getPostHog, initializePostHog } from '../src/utils/posthog';
import { fetchUser } from '../src/actions/user';

export const GlobalStyles = createGlobalStyle([
  animate,
  normalize,
  commoncss,
  toggler,
  rcslider,
  slickSlider,
]);

if (!isServer()) {
  // we only want to call this init function on the frontend, so we check typeof window !== 'undefined'
  SuperTokensReact.init(SuperTokensConfig.frontendConfig());
}

const { gtmContainerId, posthogHost, posthogKey } = getConfig().publicRuntimeConfig;

if (!isServer()) {
  initializePostHog({ apiKey: posthogKey, apiHost: posthogHost });
}

const CHUNK_ERROR_PATTERN = /Loading (CSS )?chunk \S+ failed/i;
const CHUNK_RELOAD_KEY = 'gmb.lastChunkReload';
const POSTHOG_IDENTIFIED_KEY = 'gmb.posthogIdentified';
// Long enough that a chunk which is genuinely unreachable cannot spin the tab,
// short enough that a deploy later in the same session can still self-heal.
const CHUNK_RELOAD_COOLDOWN_MS = 60 * 1000;

const isChunkLoadError = (error, message) => {
  if (error?.name === 'ChunkLoadError') {
    return true;
  }
  const text = message || error?.message || (typeof error === 'string' ? error : '');
  return CHUNK_ERROR_PATTERN.test(text);
};

// An HTML document held in cache can point at chunks a newer deploy has already
// replaced, and the only way out is a reload. Do that at most once per cooldown
// so a chunk that is permanently missing (offline, blocked, 404) cannot loop.
const reloadForChunkError = () => {
  try {
    const lastReload = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
    if (Date.now() - lastReload < CHUNK_RELOAD_COOLDOWN_MS) {
      return;
    }
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch (e) {
    // sessionStorage throws when storage is blocked (Safari private mode, cookies
    // off). Without it the retry cannot be bounded, so leave the error to Sentry.
    return;
  }

  window.location.reload();
};

// identify() re-sends person properties as a $set on every page load, so skip it
// when this browser already identified the same user with the same properties.
const readIdentifiedKey = () => {
  try {
    return window.localStorage.getItem(POSTHOG_IDENTIFIED_KEY);
  } catch (e) {
    return null;
  }
};

const writeIdentifiedKey = (key) => {
  try {
    window.localStorage.setItem(POSTHOG_IDENTIFIED_KEY, key);
  } catch (e) {
    // Storage blocked: identify again on the next load.
  }
};

const PostHogIdentity = () => {
  const dispatch = useDispatch();
  const session = useSessionContext();
  const user = useSelector(state => state.user);
  const { id, email, firstName, lastName } = user || {};
  const previousUserId = useRef(null);
  const hasSession = !session.loading && session.doesSessionExist;

  // Only some pages fetch the profile, so a visit that starts on a workout or
  // program page would otherwise stay anonymous.
  useEffect(() => {
    if (hasSession && !id) {
      dispatch(fetchUser());
    }
  }, [dispatch, hasSession, id]);

  useEffect(() => {
    if (!id) {
      return;
    }

    const distinctId = String(id);
    const name = [firstName, lastName].filter(Boolean).join(' ');
    const properties = {
      ...(email ? { email } : {}),
      ...(name ? { name } : {}),
    };

    getPostHog().then(posthog => {
      if (!posthog) {
        return;
      }

      if (previousUserId.current && previousUserId.current !== distinctId) {
        posthog.reset();
      }
      previousUserId.current = distinctId;

      const identifiedKey = JSON.stringify([distinctId, properties]);
      if (posthog.get_distinct_id() === distinctId && readIdentifiedKey() === identifiedKey) {
        return;
      }

      posthog.identify(distinctId, properties);
      writeIdentifiedKey(identifiedKey);
    });
  }, [email, firstName, id, lastName]);

  return null;
};

const Root = ({ Component, ...rest }) => {
  const { store, props } = wrapper.useWrappedStore(rest);
  const { pageProps, err } = props;
  const router = useRouter();

  useEffect(() => {
    const onRouteChangeComplete = () => {
      window.scrollTo(0, 0);
    };
    // Webpack throws chunk failures on sync imports and rejects on dynamic ones,
    // so both channels need listening to.
    const onError = (event) => {
      if (isChunkLoadError(event.error, event.message)) {
        reloadForChunkError();
      }
    };
    const onUnhandledRejection = (event) => {
      if (isChunkLoadError(event.reason)) {
        reloadForChunkError();
      }
    };

    Router.events.on('routeChangeComplete', onRouteChangeComplete);
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    return () => {
      Router.events.off('routeChangeComplete', onRouteChangeComplete);
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);

  return (
    <Provider store={store}>
      {Boolean(gtmContainerId) && (
        <Script id="gtm" strategy="afterInteractive">
          {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmContainerId}');`}
        </Script>
      )}
      <HelpscoutProvider>
        <SuperTokensWrapper>
          <PostHogIdentity />
          <SpeedInsights route={router.pathname} />
          <GlobalStyles />
          <NProgress showAfterMs={300} options={{ trickleSpeed: 50 }} />
          <Toast />
          <CommandPalette />
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <Component {...pageProps} err={err} />
        </SuperTokensWrapper>
      </HelpscoutProvider>
    </Provider>
  );
};

export default Root;
